import { Spacing } from '@persona-id/design-tokens';
import { CircleInfoIcon } from '@persona-id/design-tokens/icons';
import { ChangeEvent, FC, PropsWithChildren } from 'react';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import Tooltip from 'dashboard/components/Tooltip';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { Trigger } from 'dashboard/models/trigger';
import {
  WorkflowStep,
  WorkflowStepBase,
  WorkflowStepOnErrorAction,
} from 'dashboard/models/workflow-version';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { ApiModel } from 'lib/interfaces/api';

interface StepAdvancedConfigurationWithErrorHandlingPanelProps {
  step: WorkflowStepBase;
  updateStep: (step: WorkflowStep) => void;
  trigger?: Trigger;
  disabled?: boolean;
}

const TooltipContainer = styled.span`
  padding: ${Spacing[1]};
`;

export const StepAdvancedConfigurationWithErrorHandlingPanel: FC<
  PropsWithChildren<StepAdvancedConfigurationWithErrorHandlingPanelProps>
> = ({ updateStep, step, trigger, disabled = false, children }) => {
  const isContinueOnErrorEnabled = trigger?.type !== ApiModel.TriggerDirect;
  if (!isContinueOnErrorEnabled && (children == null || children === false)) {
    return null;
  }
  return (
    <Accordion title='Advanced Configuration' defaultExpanded={false}>
      {children}
      {isContinueOnErrorEnabled && (
        <>
          <Spacer height={2} />
          <ParamLabel size='md' labelText='Errors' />
          <Checkbox
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              updateStep({
                ...step,
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                'on-error': e.target.checked === true ? WorkflowStepOnErrorAction.Continue : null,
              });
            }}
            checked={step['on-error'] === WorkflowStepOnErrorAction.Continue}
            aria-label='Continue on error'
            disabled={disabled}
            label={
              <span>
                Continue on error
                <TooltipContainer>
                  <Tooltip
                    trigger='hover'
                    tooltip={
                      <Paragraph color='primary' size='sm'>
                        Marking a step as optional ensures that execution continues when the
                        workflow encounters an error
                      </Paragraph>
                    }
                    hideArrow={true}
                  >
                    <CircleInfoIcon />
                  </Tooltip>
                </TooltipContainer>
              </span>
            }
          />
        </>
      )}
    </Accordion>
  );
};
